import React, { useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Bot,
  CalendarCheck,
  ChevronDown,
  ChevronRight,
  CreditCard,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const NAV = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { to: '/admin/partners', label: 'Manage Partners', icon: Users, testId: 'nav-partners' },
  { to: '/admin/bookings', label: 'Manage Bookings', icon: CalendarCheck, testId: 'nav-bookings' },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard, testId: 'nav-payments' },
  { to: '/admin/compliance', label: 'Compliance', icon: ShieldCheck, testId: 'nav-compliance' },
  { to: '/admin/prateek-ai', label: 'Prateek AI Reports', icon: Bot, testId: 'nav-prateek-ai' },
];

function SidebarBrand() {
  return (
    <div className="px-6 py-5 border-b border-slate-800">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-base font-bold tracking-tight">BeautyMateUK</h1>
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Admin Console</p>
        </div>
      </div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-rose-500/15 text-rose-300 border border-rose-500/20'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white',
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

function ExternalLinks({ open, onToggle }) {
  return (
    <div className="pt-5 mt-2 border-t border-slate-800">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-wider text-slate-400 hover:text-slate-200"
        data-testid="nav-external-toggle"
      >
        <span className="flex items-center gap-2">
          <ExternalLink className="h-3.5 w-3.5" />
          External Links
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          <NavLink
            to="/customer"
            data-testid="nav-customer-app"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <ExternalLink className="h-4 w-4" /> View Customer App
          </NavLink>
          <NavLink
            to="/partner"
            data-testid="nav-partner-app"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <ExternalLink className="h-4 w-4" /> View Partner App
          </NavLink>
        </div>
      )}
    </div>
  );
}

function SidebarUser({ user, onLogout }) {
  return (
    <div className="px-4 py-4 border-t border-slate-800">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-rose-500/20 text-rose-300 flex items-center justify-center text-sm font-semibold">
          {(user?.name || 'A').slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name || 'Admin'}</p>
          <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
        onClick={onLogout}
        data-testid="btn-logout"
      >
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [externalOpen, setExternalOpen] = useState(true);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  }, [logout, navigate]);

  const toggleExternal = useCallback(() => setExternalOpen((v) => !v), []);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-slate-900 text-slate-100 flex flex-col" data-testid="admin-sidebar">
        <SidebarBrand />
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          <ExternalLinks open={externalOpen} onToggle={toggleExternal} />
        </nav>
        <SidebarUser user={user} onLogout={handleLogout} />
      </aside>

      <main className="flex-1 min-w-0 bg-slate-50">
        <div className="px-8 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
